import { useState, useEffect, useCallback } from 'react'
import { api, getDataAPI, postDataAPI } from '../lib/api'
import { uploadImage } from '../lib/uploadImage'
import Button from '../components/ui/Button'
import Modal from '../components/ui/Modal'
import Alert from '../components/ui/Alert'
import { useAccessibility, type FontSize } from '../hooks/useAccessibility'
import { useTheme } from '../context/ThemeContext'

// Email change flow step type
type EmailChangeStep = 'idle' | 'verify-current' | 'enter-new' | 'verify-new' | 'success'

export default function SettingsPage() {

  const [isGoogleConnected, setIsGoogleConnected] = useState(false)
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false)
  const [isDisconnectingGoogle, setIsDisconnectingGoogle] = useState(false)

  const [isSheetsOpen, setIsSheetsOpen] = useState(false)
  const [sheetsLoading, setSheetsLoading] = useState(false)
  const [workspaceId, setWorkspaceId] = useState('')
  const [secretToken, setSecretToken] = useState('')
  const [webhookUrl, setWebhookUrl] = useState('')
  const [bidirectionalWebhookUrl, setBidirectionalWebhookUrl] = useState('')
  const [copiedField, setCopiedField] = useState<null | 'workspaceId' | 'secretToken' | 'webhookUrl'>(null)

  const { theme, toggleTheme } = useTheme()

  // Accessibility hook
  const { settings: accessibilitySettings, updateSetting: updateAccessibilitySetting, resetSettings: resetAccessibilitySettings } = useAccessibility()

  // Personal settings state
  const [user, setUser] = useState<any>(() => {
    try {
      const raw = localStorage.getItem('dizeeUser')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })
  const [profileName, setProfileName] = useState('')
  const [profileEmail, setProfileEmail] = useState('')
  const [avatar, setAvatar] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null)
  const [profileError, setProfileError] = useState<string | null>(null)

  // ── Email change modal state ──
  const [emailChangeStep, setEmailChangeStep] = useState<EmailChangeStep>('idle')
  const [emailChangeOtp, setEmailChangeOtp] = useState('')
  const [emailChangeNewEmail, setEmailChangeNewEmail] = useState('')
  const [emailChangeError, setEmailChangeError] = useState<string | null>(null)
  const [emailChangeLoading, setEmailChangeLoading] = useState(false)
  const [emailChangeResendTimer, setEmailChangeResendTimer] = useState(0)

  const isEmailChangeOpen = emailChangeStep !== 'idle'

  // Resend countdown timer
  useEffect(() => {
    if (emailChangeResendTimer <= 0) return
    const t = window.setTimeout(() => setEmailChangeResendTimer((s) => s - 1), 1000)
    return () => window.clearTimeout(t)
  }, [emailChangeResendTimer])

  const maskEmail = (email: string) => {
    if (!email || !email.includes('@')) return email
    const [local, domain] = email.split('@')
    return `${local.slice(0, 2)}${'*'.repeat(Math.max(local.length - 2, 3))}@${domain}`
  }

  const resetEmailChangeState = useCallback(() => {
    setEmailChangeStep('idle')
    setEmailChangeOtp('')
    setEmailChangeNewEmail('')
    setEmailChangeError(null)
    setEmailChangeLoading(false)
    setEmailChangeResendTimer(0)
  }, [])

  // Step 1: Start email change - send OTP to current email
  const handleStartEmailChange = async () => {
    const currentEmail = user?.email
    if (!currentEmail) return
    setEmailChangeError(null)
    setEmailChangeLoading(true)
    try {
      await postDataAPI('auth/sendOtpToEmail', { email: currentEmail })
      setEmailChangeStep('verify-current')
      setEmailChangeResendTimer(30)
      setEmailChangeOtp('')
    } catch (err: any) {
      setEmailChangeError(err?.response?.data?.message || err?.message || 'Failed to send verification code')
    } finally {
      setEmailChangeLoading(false)
    }
  }

  // Step 2: Verify current email OTP
  const handleVerifyCurrentEmail = async () => {
    const currentEmail = user?.email
    if (!currentEmail || emailChangeOtp.trim().length !== 6) return
    setEmailChangeError(null)
    setEmailChangeLoading(true)
    try {
      const res = await postDataAPI('auth/verifyOtpForEmail', { email: currentEmail, code: emailChangeOtp.trim() })
      if (res?.status === 200) {
        setEmailChangeStep('enter-new')
        setEmailChangeOtp('')
      } else {
        setEmailChangeError(res?.message || 'Verification failed. Please try again.')
      }
    } catch (err: any) {
      setEmailChangeError(err?.response?.data?.message || err?.message || 'Invalid code. Please try again.')
    } finally {
      setEmailChangeLoading(false)
    }
  }

  // Step 3: Send OTP to new email
  const handleSendNewEmailOtp = async () => {
    const newEmail = emailChangeNewEmail.trim()
    if (!newEmail || !newEmail.includes('@')) {
      setEmailChangeError('Please enter a valid email address.')
      return
    }
    if (newEmail.toLowerCase() === user?.email?.toLowerCase()) {
      setEmailChangeError('New email must be different from your current email.')
      return
    }
    setEmailChangeError(null)
    setEmailChangeLoading(true)
    try {
      await postDataAPI('auth/sendOtpToEmail', { email: newEmail })
      setEmailChangeStep('verify-new')
      setEmailChangeResendTimer(30)
      setEmailChangeOtp('')
    } catch (err: any) {
      setEmailChangeError(err?.response?.data?.message || err?.message || 'Failed to send verification code to new email')
    } finally {
      setEmailChangeLoading(false)
    }
  }

  // Step 4: Verify new email OTP and save
  const handleVerifyNewEmailAndSave = async () => {
    const newEmail = emailChangeNewEmail.trim()
    if (!newEmail || emailChangeOtp.trim().length !== 6) return
    setEmailChangeError(null)
    setEmailChangeLoading(true)
    try {
      const res = await postDataAPI('auth/verifyOtpForEmail', { email: newEmail, code: emailChangeOtp.trim() })
      if (res?.status === 200) {
        // Both emails verified - now update the user's email
        // Use api.put() directly to:
        //   1) match the backend's PUT route (not POST)
        //   2) bypass userId injection so team members update their OWN email
        await api.put('user/update-user-article', { email: newEmail })

        // Update local state
        const updatedUser = { ...user, email: newEmail }
        setUser(updatedUser)
        setProfileEmail(newEmail)
        localStorage.setItem('dizeeUser', JSON.stringify(updatedUser))

        setEmailChangeStep('success')
      } else {
        setEmailChangeError(res?.message || 'Verification failed. Please try again.')
      }
    } catch (err: any) {
      setEmailChangeError(err?.response?.data?.message || err?.message || 'Invalid code. Please try again.')
    } finally {
      setEmailChangeLoading(false)
    }
  }

  // Resend OTP for whichever step we're on
  const handleResendEmailOtp = async () => {
    if (emailChangeResendTimer > 0) return
    const targetEmail = emailChangeStep === 'verify-current' ? user?.email : emailChangeNewEmail.trim()
    if (!targetEmail) return
    setEmailChangeError(null)
    try {
      await postDataAPI('auth/sendOtpToEmail', { email: targetEmail })
      setEmailChangeResendTimer(30)
      setEmailChangeOtp('')
    } catch (err: any) {
      setEmailChangeError(err?.response?.data?.message || err?.message || 'Failed to resend code')
    }
  }

  // Load user data on mount
  useEffect(() => {
    const loadedUser = (() => {
      try {
        const raw = localStorage.getItem('dizeeUser')
        return raw ? JSON.parse(raw) : null
      } catch {
        return null
      }
    })()
    if (loadedUser) {
      setUser(loadedUser)
      setProfileName(loadedUser.name || '')
      setProfileEmail(loadedUser.email || '')
      setAvatarPreview(loadedUser.avatar || null)
    }
  }, [])

  // Check if user is team member acting as artist
  const isTeamMember = user?.professionRole && ['manager', 'support'].includes(user.professionRole)
  const selectedArtist = (() => {
    try {
      const raw = localStorage.getItem('selectedArtist')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })()
  const isActingAsArtist = isTeamMember && !!selectedArtist


  const asRecord = (v: unknown): Record<string, unknown> | null =>
    v && typeof v === 'object' ? (v as Record<string, unknown>) : null

  const extractConnected = (payload: unknown): boolean => {
    const p = asRecord(payload)
    const d1 = asRecord(p?.['data'])
    const d2 = asRecord(d1?.['data'])
    const conn = asRecord(d2?.['connection'])
    const v = d2?.['connected'] ?? d1?.['connected'] ?? p?.['connected'] ?? conn?.['connected'] ?? false
    return Boolean(v)
  }

  const refreshGoogleStatus = async () => {
    try {
      const status = await getDataAPI('user/connectors/googleCalendar/status')
      setIsGoogleConnected(extractConnected(status))
    } catch (e) {
      console.error('Failed to load Google connection status:', e)
      setIsGoogleConnected(false)
    }
  }

  useEffect(() => {
    refreshGoogleStatus()
  }, [])

  const handleConnectGoogle = async () => {
    setIsConnectingGoogle(true)
    try {
      sessionStorage.setItem('googleConnecting', '1')
      const redirectUri = `${window.location.origin}/google-calendar-callback`
      const resp = await getDataAPI(
        `user/connectors/googleCalendar/auth-url?redirectUri=${encodeURIComponent(redirectUri)}`
      )
      const authUrl =
        resp?.data?.data?.authUrl ?? resp?.data?.authUrl ?? resp?.authUrl ?? resp?.data?.data?.url ?? resp?.url
      if (!authUrl || typeof authUrl !== 'string') {
        window.alert('Failed to initiate Google connection.')
        return
      }
      window.location.href = authUrl
    } finally {
      setIsConnectingGoogle(false)
    }
  }

  const handleDisconnectGoogle = async () => {
    const ok = window.confirm('Disconnect Google? This will remove Calendar and Gmail access.')
    if (!ok) return
    setIsDisconnectingGoogle(true)
    try {
      await postDataAPI('user/connectors/googleCalendar/disconnect', {})
      setIsGoogleConnected(false)
    } catch (e) {
      console.error('Failed to disconnect Google:', e)
      window.alert('Failed to disconnect Google. Please try again.')
    } finally {
      setIsDisconnectingGoogle(false)
    }
  }

  const computeDefaultWebhookUrl = () => {
    const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'
    return `${backendUrl.replace(/\/$/, '')}/api/v1/integrations/google-sheets/sync`
  }

  const loadSheetsSettings = async () => {
    setSheetsLoading(true)
    try {
      const resp = await getDataAPI('integrations/google-sheets/settings')
      const r0 = asRecord(resp)
      const r1 = asRecord(r0?.['data'])
      const r2 = asRecord(r1?.['data']) ?? r1 ?? r0
      setWorkspaceId(typeof r2?.['workspaceId'] === 'string' ? (r2['workspaceId'] as string) : '')
      setSecretToken(typeof r2?.['secretToken'] === 'string' ? (r2['secretToken'] as string) : '')
      setWebhookUrl(typeof r2?.['webhookUrl'] === 'string' ? (r2['webhookUrl'] as string) : computeDefaultWebhookUrl())
      setBidirectionalWebhookUrl(typeof r2?.['bidirectionalWebhookUrl'] === 'string' ? (r2['bidirectionalWebhookUrl'] as string) : '')
    } catch (e) {
      console.error('Error loading Google Sheets settings:', e)
      setWebhookUrl(computeDefaultWebhookUrl())
    } finally {
      setSheetsLoading(false)
    }
  }

  const saveSheetsSettings = async (next: Partial<{ workspaceId: string; bidirectionalWebhookUrl: string }>) => {
    const payload: { workspaceId?: string; bidirectionalWebhookUrl?: string | null } = {}
    if (typeof next.workspaceId === 'string') payload.workspaceId = next.workspaceId.trim()
    if (typeof next.bidirectionalWebhookUrl === 'string') payload.bidirectionalWebhookUrl = next.bidirectionalWebhookUrl.trim() || null
    await postDataAPI('integrations/google-sheets/settings', payload)
  }

  const regenerateSheetsSecret = async () => {
    try {
      await postDataAPI('integrations/google-sheets/regenerate-secret', {
        workspaceId: workspaceId.trim() || undefined,
      })
      await loadSheetsSettings()
    } catch (e) {
      console.error('Failed to regenerate secret:', e)
      window.alert('Failed to regenerate secret token. Please try again.')
    }
  }

  const handleCopy = async (text: string, field: 'workspaceId' | 'secretToken' | 'webhookUrl') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      window.setTimeout(() => setCopiedField(null), 1500)
    } catch (e) {
      console.error('Failed to copy:', e)
      window.alert('Failed to copy to clipboard.')
    }
  }


  const handleResetOnboarding = () => {
    ;['artist', 'manager', 'support'].forEach((role) => {
      localStorage.removeItem(`dizee-onboarding-${role}`)
    })
    window.alert('Onboarding tutorial reset. Reload to see it again.')
  }


  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setAvatar(file)
      const reader = new FileReader()
      reader.onloadend = () => setAvatarPreview(reader.result as string)
      reader.readAsDataURL(file)
    }
  }

  const handleSaveProfile = async () => {
    setIsSavingProfile(true)
    setProfileError(null)
    setProfileSuccess(null)
    try {
      let avatarUrl = user?.avatar || avatarPreview
      
      // Only upload if there's a new file
      if (avatar) {
        try {
          const uploaded = await uploadImage(avatar)
          if (uploaded) avatarUrl = uploaded
        } catch (err) {
          console.error('Avatar upload failed:', err)
          setProfileError('Failed to upload avatar. Continuing with other changes.')
        }
      }

      // Note: email is NOT updated here -- it uses its own secure OTP flow
      const updateData: any = {
        name: profileName.trim(),
      }
      if (avatarUrl) updateData.avatar = avatarUrl

      // Use api.put() directly to:
      //   1) match the backend's PUT route (not POST -- the legacy uses putDataAPI here)
      //   2) bypass userId injection so team members update their OWN profile, not the artist's
      // Auth token is handled by the axios interceptor automatically.
      const res = await api.put('user/update-user-article', updateData)
      
      const resData = res?.data
      if (resData?.status === 200 || resData?.data?.status === 200 || resData?.data?.user) {
        const updatedUser = { ...user, ...updateData }
        setUser(updatedUser)
        localStorage.setItem('dizeeUser', JSON.stringify(updatedUser))
        setAvatar(null) // Clear file input
        setProfileSuccess('Profile updated successfully!')
      } else {
        setProfileError(resData?.message || resData?.data?.message || 'Failed to update profile')
      }
    } catch (err: any) {
      console.error('Profile update error:', err)
      setProfileError(err?.response?.data?.message || err?.message || 'Failed to update profile. Please try again.')
    } finally {
      setIsSavingProfile(false)
    }
  }

  const sheetsConfigured = Boolean(secretToken)
  const sheetsTemplateId =
    import.meta.env.VITE_GOOGLE_SHEETS_TEMPLATE_ID || '1FoYR6rZzvxBXshWZXREB-1P1Mg9udLcGPFFJmzOACj8'
  const sheetsTemplateCopyUrl = `https://docs.google.com/spreadsheets/d/${sheetsTemplateId}/copy`

  return (
    <div className="p-4 pt-4 md:p-8 max-w-6xl mx-auto">
      {/* Sticky page header */}
      <div className="sticky top-0 z-10 -mx-4 md:-mx-8 px-4 md:px-8 pt-16 md:pt-0 pb-4 bg-[rgb(var(--bg-rgb))]">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2 text-gray-900 dark:text-white">Settings</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {isActingAsArtist 
              ? `Managing settings for ${selectedArtist?.name || 'artist'}`
              : 'Manage your personal settings and preferences'
            }
          </p>
        </div>
      </div>

      {/* Account Settings - Visible for all users */}
      <section className="mb-10">
        <h2 className="text-sm font-medium mb-6 text-gray-900 dark:text-white">Account</h2>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-6">
          {profileSuccess && (
            <Alert type="success" message={profileSuccess} onDismiss={() => setProfileSuccess(null)} />
          )}
          {profileError && (
            <Alert type="error" message={profileError} onDismiss={() => setProfileError(null)} />
          )}

          {/* Avatar */}
          <div>
            <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-white">Profile Picture</label>
            <div className="flex items-center gap-4">
              <div className="relative">
                <div
                  className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden"
                  style={{ background: avatarPreview ? 'transparent' : 'linear-gradient(45deg, #6366f1, #8b5cf6)' }}
                >
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white text-xl font-semibold">
                      {profileName ? profileName.charAt(0).toUpperCase() : 'U'}
                    </span>
                  )}
                </div>
                <label
                  htmlFor="profile-avatar"
                  className="absolute bottom-0 right-0 w-6 h-6 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-700 dark:hover:bg-gray-200 transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </label>
                <input
                  id="profile-avatar"
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                <p>Upload a profile picture</p>
                <p className="text-xs">JPG, PNG or GIF (max 5MB)</p>
              </div>
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Name</label>
            <input
              type="text"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent"
            />
          </div>

          {/* Email - read-only display with Change button */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Email</label>
            <div className="flex items-center gap-3">
              <div className="flex-1 px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white">
                {profileEmail || 'No email set'}
              </div>
              <Button
                variant="secondary"
                onClick={handleStartEmailChange}
                disabled={emailChangeLoading}
              >
                {emailChangeLoading && emailChangeStep === 'idle' ? 'Sending…' : 'Change'}
              </Button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
              Email changes require verification of both your current and new email addresses.
            </p>
          </div>

          {/* Role Badge - only for team members */}
          {isTeamMember && (
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Account Type</label>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                user?.professionRole === 'manager'
                  ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                  : user?.professionRole === 'support'
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
              }`}>
                {user?.professionRole === 'manager' ? 'Manager' : user?.professionRole === 'support' ? 'Support' : 'Artist'}
              </span>
            </div>
          )}

          {/* Save Button (for name / avatar changes) */}
          <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-gray-700">
            <Button onClick={handleSaveProfile} disabled={isSavingProfile}>
              {isSavingProfile ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </section>

      {/* ── Email Change Modal ── */}
      <Modal
        isOpen={isEmailChangeOpen}
        onClose={resetEmailChangeState}
        title={
          emailChangeStep === 'verify-current' ? 'Verify your current email' :
          emailChangeStep === 'enter-new' ? 'Enter your new email' :
          emailChangeStep === 'verify-new' ? 'Verify your new email' :
          emailChangeStep === 'success' ? 'Email updated' :
          'Change email'
        }
        description={
          emailChangeStep === 'verify-current'
            ? `We sent a 6-digit code to ${maskEmail(user?.email || '')}. Enter it below to confirm your identity.`
            : emailChangeStep === 'enter-new'
              ? 'Enter the new email address you want to use for your account.'
              : emailChangeStep === 'verify-new'
                ? `We sent a 6-digit code to ${maskEmail(emailChangeNewEmail)}. Enter it below to confirm you own this email.`
                : emailChangeStep === 'success'
                  ? 'Your email has been successfully updated.'
                  : undefined
        }
      >
        <div className="space-y-4">
          {emailChangeError && (
            <Alert type="error" message={emailChangeError} onDismiss={() => setEmailChangeError(null)} autoDismiss={false} />
          )}

          {/* Step 1: Verify current email */}
          {emailChangeStep === 'verify-current' && (
            <>
              {/* Step indicator */}
              <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 mb-2">
                <span className="w-5 h-5 rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 flex items-center justify-center font-semibold">1</span>
                <span className="text-gray-900 dark:text-white font-medium">Verify current email</span>
                <span className="mx-1 text-gray-300 dark:text-gray-600">&rarr;</span>
                <span>New email</span>
                <span className="mx-1 text-gray-300 dark:text-gray-600">&rarr;</span>
                <span>Verify new email</span>
              </div>
              <div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={emailChangeOtp}
                  onChange={(e) => setEmailChangeOtp(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  placeholder="6-digit code"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent tracking-widest text-center text-lg"
                  autoFocus
                />
              </div>
              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={handleResendEmailOtp}
                  disabled={emailChangeResendTimer > 0}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 transition-colors"
                >
                  {emailChangeResendTimer > 0 ? `Resend in ${emailChangeResendTimer}s` : 'Resend code'}
                </button>
                <Button
                  onClick={handleVerifyCurrentEmail}
                  disabled={emailChangeOtp.trim().length !== 6 || emailChangeLoading}
                >
                  {emailChangeLoading ? 'Verifying…' : 'Verify'}
                </Button>
              </div>
            </>
          )}

          {/* Step 2: Enter new email */}
          {emailChangeStep === 'enter-new' && (
            <>
              <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 mb-2">
                <span className="w-5 h-5 rounded-full bg-green-600 text-white flex items-center justify-center">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                </span>
                <span className="text-green-600 dark:text-green-400 font-medium">Current email verified</span>
                <span className="mx-1 text-gray-300 dark:text-gray-600">&rarr;</span>
                <span className="w-5 h-5 rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 flex items-center justify-center font-semibold">2</span>
                <span className="text-gray-900 dark:text-white font-medium">New email</span>
                <span className="mx-1 text-gray-300 dark:text-gray-600">&rarr;</span>
                <span>Verify</span>
              </div>
              <div>
                <input
                  type="email"
                  value={emailChangeNewEmail}
                  onChange={(e) => setEmailChangeNewEmail(e.target.value)}
                  placeholder="new@email.com"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent"
                  autoFocus
                />
              </div>
              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSendNewEmailOtp}
                  disabled={!emailChangeNewEmail.trim() || !emailChangeNewEmail.includes('@') || emailChangeLoading}
                >
                  {emailChangeLoading ? 'Sending…' : 'Send verification code'}
                </Button>
              </div>
            </>
          )}

          {/* Step 3: Verify new email */}
          {emailChangeStep === 'verify-new' && (
            <>
              <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 mb-2">
                <span className="w-5 h-5 rounded-full bg-green-600 text-white flex items-center justify-center">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                </span>
                <span className="text-green-600 dark:text-green-400">Verified</span>
                <span className="mx-1 text-gray-300 dark:text-gray-600">&rarr;</span>
                <span className="w-5 h-5 rounded-full bg-green-600 text-white flex items-center justify-center">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                </span>
                <span className="text-green-600 dark:text-green-400">{maskEmail(emailChangeNewEmail)}</span>
                <span className="mx-1 text-gray-300 dark:text-gray-600">&rarr;</span>
                <span className="w-5 h-5 rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 flex items-center justify-center font-semibold">3</span>
                <span className="text-gray-900 dark:text-white font-medium">Verify new email</span>
              </div>
              <div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={emailChangeOtp}
                  onChange={(e) => setEmailChangeOtp(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  placeholder="6-digit code"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent tracking-widest text-center text-lg"
                  autoFocus
                />
              </div>
              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={handleResendEmailOtp}
                  disabled={emailChangeResendTimer > 0}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 transition-colors"
                >
                  {emailChangeResendTimer > 0 ? `Resend in ${emailChangeResendTimer}s` : 'Resend code'}
                </button>
                <Button
                  onClick={handleVerifyNewEmailAndSave}
                  disabled={emailChangeOtp.trim().length !== 6 || emailChangeLoading}
                >
                  {emailChangeLoading ? 'Saving…' : 'Verify & Update Email'}
                </Button>
              </div>
            </>
          )}

          {/* Success */}
          {emailChangeStep === 'success' && (
            <div className="text-center py-4">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 mx-auto flex items-center justify-center mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Your email has been changed to <strong className="text-gray-900 dark:text-white">{profileEmail}</strong>.
              </p>
              <Button onClick={resetEmailChangeState}>Done</Button>
            </div>
          )}
        </div>
      </Modal>

      {/* Artist Account Settings - Only visible when acting as artist OR for actual artists */}
      {(!isTeamMember || isActingAsArtist) && (
        <>
          {isActingAsArtist && (
            <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30 rounded-lg p-4">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Note:</strong> These settings apply to {selectedArtist?.name || 'the artist'}'s account only.
              </p>
            </div>
          )}

          <section className="mb-10">
            <h2 className="text-sm font-medium mb-6 text-gray-900 dark:text-white">Onboarding</h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between gap-6">
              <div>
                <h3 className="text-base font-medium mb-1 text-gray-900 dark:text-white">Reset onboarding tutorial</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Clears the first-time tutorial so you can preview it again.
                </p>
              </div>
              <Button variant="secondary" onClick={handleResetOnboarding}>
                Reset tutorial
              </Button>
            </div>
          </section>

      {/* Google (Calendar + Gmail) */}
      <section className="mb-10">
        <h2 className="text-sm font-medium mb-6 text-gray-900 dark:text-white">
          {isActingAsArtist ? 'Artist Account - Google' : 'Google'}
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h3 className="text-base font-medium mb-1 text-gray-900 dark:text-white">Google Calendar + Gmail</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Connect once to enable Calendar sync and email features.</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Status:</span>
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    isGoogleConnected ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}
                >
                  {isGoogleConnected ? 'Connected' : 'Not Connected'}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              {isGoogleConnected ? (
                <Button variant="secondary" onClick={handleDisconnectGoogle} disabled={isDisconnectingGoogle}>
                  {isDisconnectingGoogle ? 'Disconnecting…' : 'Disconnect'}
                </Button>
              ) : (
                <Button onClick={handleConnectGoogle} disabled={isConnectingGoogle}>
                  {isConnectingGoogle ? 'Connecting…' : 'Connect'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* API Key Management */}
      <section className="mb-10">
        <h2 className="text-sm font-medium mb-6 text-gray-900 dark:text-white">
          {isActingAsArtist ? 'Artist Account - API Key Management' : 'API Key Management'}
        </h2>
        
        <div className="space-y-4">
          {/* Google Sheets */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="text-base font-medium mb-1 text-gray-900 dark:text-white">Google Sheets</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  Used for syncing shows data with Google Sheets.
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Status:</span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    sheetsConfigured ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}>
                    {sheetsConfigured ? 'Configured' : 'Not Configured'}
                  </span>
                </div>
              </div>
              <Button
                onClick={async () => {
                  setIsSheetsOpen(true)
                  await loadSheetsSettings()
                }}
              >
                Configure
              </Button>
            </div>
          </div>

        </div>
      </section>

      <Modal
        isOpen={isSheetsOpen}
        onClose={() => setIsSheetsOpen(false)}
        title="Google Sheets Sync"
        maxWidthClassName="max-w-xl"
      >
        <div className="space-y-5">
          <p className="text-sm text-gray-600">
            Use the Dizee Google Sheets template and connect it with your Workspace ID + Secret Token. The sheet will
            POST changes to the webhook URL shown below.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Workspace ID</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={workspaceId}
                  onChange={(e) => setWorkspaceId(e.target.value)}
                  onBlur={async () => {
                    try {
                      await saveSheetsSettings({ workspaceId })
                    } catch (e) {
                      console.error('Failed to save workspaceId:', e)
                      window.alert('Failed to save Workspace ID.')
                    }
                  }}
                  placeholder="Enter your workspace ID"
                  className="ds-input flex-1"
                  disabled={sheetsLoading}
                />
                <Button
                  variant="secondary"
                  type="button"
                  disabled={!workspaceId}
                  onClick={() => handleCopy(workspaceId, 'workspaceId')}
                >
                  {copiedField === 'workspaceId' ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">Secret Token</label>
                <button
                  type="button"
                  className="text-xs text-gray-600 hover:text-gray-900 underline underline-offset-4"
                  onClick={regenerateSheetsSecret}
                  disabled={sheetsLoading}
                >
                  Regenerate
                </button>
              </div>
              <div className="flex gap-2">
                <input type="text" value={secretToken} readOnly className="ds-input flex-1" />
                <Button
                  variant="secondary"
                  type="button"
                  disabled={!secretToken}
                  onClick={() => handleCopy(secretToken, 'secretToken')}
                >
                  {copiedField === 'secretToken' ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Webhook URL</label>
              <div className="flex gap-2">
                <input type="text" value={webhookUrl} readOnly className="ds-input flex-1" />
                <Button
                  variant="secondary"
                  type="button"
                  disabled={!webhookUrl}
                  onClick={() => handleCopy(webhookUrl, 'webhookUrl')}
                >
                  {copiedField === 'webhookUrl' ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Bidirectional Sync Webhook URL (optional)</label>
              <input
                type="text"
                value={bidirectionalWebhookUrl}
                onChange={(e) => setBidirectionalWebhookUrl(e.target.value)}
                onBlur={async () => {
                  try {
                    await saveSheetsSettings({ bidirectionalWebhookUrl })
                  } catch (e) {
                    console.error('Failed to save bidirectionalWebhookUrl:', e)
                    window.alert('Failed to save bidirectional webhook URL.')
                  }
                }}
                placeholder="https://script.google.com/macros/s/..."
                className="ds-input w-full"
                disabled={sheetsLoading}
              />
              <p className="text-xs text-gray-500 mt-2">
                If you deploy a Google Apps Script web app for Dizee → Sheets updates, paste its URL here.
              </p>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              className="flex-1"
              onClick={() => window.open(sheetsTemplateCopyUrl, '_blank')}
            >
              Open Template
            </Button>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsSheetsOpen(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* Accessibility Settings */}
      <section className="mb-10">
        <h2 className="text-sm font-medium mb-6 text-gray-900 dark:text-white">
          {isActingAsArtist ? 'Artist Account - Accessibility' : 'Accessibility'}
        </h2>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-6">
          {/* Font Size */}
          <div>
            <label className="block text-sm font-medium mb-3 text-gray-900">Font Size</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(['small', 'medium', 'large', 'xlarge'] as FontSize[]).map((size) => (
                <button
                  key={size}
                  onClick={() => updateAccessibilitySetting('fontSize', size)}
                  className={`px-4 py-3 text-sm rounded-lg border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(var(--text-rgb),0.35)] ${
                    accessibilitySettings.fontSize === size
                      ? 'bg-[rgba(var(--text-rgb),0.08)] text-[rgb(var(--text-rgb))] border-[rgba(var(--text-rgb),0.45)]'
                      : 'bg-[rgb(var(--surface-rgb))] text-[rgb(var(--text-rgb))] border-[rgb(var(--border-rgb))] hover:bg-[rgba(var(--text-rgb),0.06)] hover:border-[rgba(var(--text-rgb),0.35)]'
                  }`}
                >
                  {size === 'xlarge' ? 'XL' : size.charAt(0).toUpperCase() + size.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* High Contrast */}
          <div className="flex items-center justify-between py-3 border-t border-gray-100">
            <div>
              <h3 className="text-sm font-medium text-gray-900">High Contrast</h3>
              <p className="text-xs text-gray-500 mt-1">Increase contrast for better visibility</p>
            </div>
            <button
              onClick={() => updateAccessibilitySetting('highContrast', !accessibilitySettings.highContrast)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(var(--text-rgb),0.35)] ${
                accessibilitySettings.highContrast
                  ? 'bg-[rgb(var(--text-rgb))]'
                  : 'bg-[rgb(var(--border-rgb))]'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  accessibilitySettings.highContrast ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Dyslexic Font */}
          <div className="flex items-center justify-between py-3 border-t border-gray-100">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Dyslexia-Friendly Font</h3>
              <p className="text-xs text-gray-500 mt-1">Use OpenDyslexic font for easier reading</p>
            </div>
            <button
              onClick={() => updateAccessibilitySetting('dyslexicFont', !accessibilitySettings.dyslexicFont)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(var(--text-rgb),0.35)] ${
                accessibilitySettings.dyslexicFont
                  ? 'bg-[rgb(var(--text-rgb))]'
                  : 'bg-[rgb(var(--border-rgb))]'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  accessibilitySettings.dyslexicFont ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Theme */}
          <div className="flex items-center justify-between py-3 border-t border-gray-100">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Theme</h3>
              <p className="text-xs text-gray-500 mt-1">Use the same light/dark mode as the sidebar</p>
            </div>
            <button
              onClick={toggleTheme}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(var(--text-rgb),0.35)] ${
                theme === 'dark' ? 'bg-[rgb(var(--text-rgb))]' : 'bg-[rgb(var(--border-rgb))]'
              }`}
              aria-label="Toggle theme"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  theme === 'dark' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Reduce Motion */}
          <div className="flex items-center justify-between py-3 border-t border-gray-100">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Reduce Motion</h3>
              <p className="text-xs text-gray-500 mt-1">Disable animations and transitions</p>
            </div>
            <button
              onClick={() => updateAccessibilitySetting('reduceMotion', !accessibilitySettings.reduceMotion)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(var(--text-rgb),0.35)] ${
                accessibilitySettings.reduceMotion
                  ? 'bg-[rgb(var(--text-rgb))]'
                  : 'bg-[rgb(var(--border-rgb))]'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  accessibilitySettings.reduceMotion ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Reset Button */}
          <div className="pt-4 border-t border-gray-100">
            <Button variant="secondary" onClick={resetAccessibilitySettings} className="w-full">
              Reset to Default
            </Button>
          </div>
        </div>
      </section>

        </>
      )}

    </div>
  )
}
